import { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from '../Context/AuthContext';
import { useMasterDataContext } from '../Context/masterDataContext';

import { Form } from "react-bootstrap";
import { 
  ChevronRight, 
  Search, 
  Home, 
  Settings, 
  Users, 
  FileText, 
  BarChart, 
  Package, 
  ShoppingCart, 
  Briefcase,
  User,
  Menu,
  X
} from "lucide-react";

// Updated CSS with proper toggle functionality
const sidebarStyles = `
<style>
  .flyout-sidebar-wrapper {
    display: flex;
    height: 100vh;
    background: #f9fafb;
    overflow: hidden;
  }

  .flyout-sidebar {
    width: 260px;
    background: white;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    height: 100%;
    border-right: 1px solid #e5e7eb;
    position: fixed;
    left: 0;
    top: 60px;
    bottom: 0;
    z-index: 40;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* CRITICAL: Toggle state - hide sidebar */
  body.toggle-sidebar .flyout-sidebar {
    transform: translateX(-260px);
  }

  /* CRITICAL: Adjust main content when sidebar is hidden */
  body.toggle-sidebar .flyout-main-content {
    margin-left: 0 !important;
  }

  /* Hide flyout menus when sidebar is collapsed */
  body.toggle-sidebar .flyout-submenu {
    display: none !important;
  }

  .flyout-sidebar-header {
    padding: 24px;
    border-bottom: 1px solid #e5e7eb;
  }

  .flyout-sidebar-title {
    font-size: 24px;
    font-weight: 700;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .flyout-search-container {
    padding: 16px;
  }

  .flyout-search-wrapper {
    position: relative;
  }

  .flyout-search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    pointer-events: none;
    z-index: 1;
  }

  .flyout-search-input {
    padding-left: 40px !important;
    padding-right: 16px !important;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    width: 100%;
    border: 2px solid #e5e7eb !important;
    border-radius: 8px !important;
    background: #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    transform: scale(1);
    transition: 
      transform 0.25s ease,
      box-shadow 0.25s ease,
      border-color 0.25s ease;
  }

  .flyout-search-input:hover {
    transform: scale(1.04);
    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.2);
    border-color: #c7d2fe;
  }

  .flyout-search-input:focus {
    outline: none !important;
    transform: scale(1.05);
    box-shadow:
      0 10px 24px rgba(99, 102, 241, 0.25),
      0 0 0 3px rgba(99, 102, 241, 0.15) !important;
    border-color: #6366f1 !important;
  }

  .flyout-search-wrapper:hover .flyout-search-icon {
    color: #6366f1;
    transition: color 0.25s ease;
  }

  .flyout-menu-container {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
  }

  .flyout-menu-item-wrapper {
    position: relative;
    margin-bottom: 4px;
  }

  .flyout-menu-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .flyout-menu-item:hover {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.2) 50%, rgba(139, 92, 246, 0.3) 100%);
    transform: translateX(4px);
    box-shadow: 4px 0 12px rgba(99, 102, 241, 0.15);
  }

  .flyout-menu-item-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .flyout-menu-item-icon {
    color: #6366f1;
    transition: color 0.3s ease;
  }

  .flyout-menu-item:hover .flyout-menu-item-icon {
    color: #8b5cf6;
  }

  .flyout-menu-item-text {
    font-weight: 500;
    color: #374151;
  }

  .flyout-menu-item:hover .flyout-menu-item-text {
    color: #111827;
  }

  .flyout-menu-item-chevron {
    color: #9ca3af;
    transition: color 0.3s ease;
  }

  .flyout-menu-item:hover .flyout-menu-item-chevron {
    color: #6366f1;
  }

  .flyout-submenu {
    position: fixed;
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    border: 1px solid #e5e7eb;
    padding: 8px;
    min-width: 240px;
    z-index: 9999;
    animation: flyoutSlide 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    max-height: 400px;
    overflow-y: auto;
  }

  @keyframes flyoutSlide {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .flyout-submenu-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    margin: 4px 8px;
    border-radius: 8px;
    text-decoration: none;
    color: #374151;
    transition: all 0.2s ease;
    cursor: pointer;
  }

  .flyout-submenu-item:hover {
    background: linear-gradient(135deg, #999aee 50%, #ad91ee 100%);
    color: white !important;
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
    text-decoration: none;
  }

  .flyout-submenu-item.active {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white !important;
  }

  .flyout-submenu-item-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .flyout-profile-section {
    padding: 16px;
    border-top: 1px solid #e5e7eb;
  }

  .flyout-profile-card {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border-radius: 12px;
    padding: 16px;
    color: white;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .flyout-profile-card:hover {
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.3);
    transform: translateY(-2px);
  }

  .flyout-profile-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
  }

  .flyout-profile-avatar {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .flyout-profile-details {
    flex: 1;
    min-width: 0;
  }

  .flyout-profile-name {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .flyout-profile-email {
    font-size: 12px;
    opacity: 0.9;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .flyout-main-content {
    flex: 1;
    margin-left: 260px;
    margin-bottom: 30px;
    overflow: auto;
    background: #f9fafb;
    padding: 24px;
    transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Hide mobile toggle - using topbar menu button instead */
  .flyout-mobile-toggle {
    display: none !important;
  }

  /* RESPONSIVE: Mobile screens (< 768px) */
  @media (max-width: 768px) {
    /* Sidebar hidden by default on mobile */
    .flyout-sidebar {
      transform: translateX(-260px);
    }

    /* When sidebar is open on mobile (toggle-sidebar class removed) */
    body:not(.toggle-sidebar) .flyout-sidebar {
      transform: translateX(0);
    }

    /* Main content always full width on mobile */
    .flyout-main-content {
      margin-left: 0 !important;
    }

    body.toggle-sidebar .flyout-main-content {
      margin-left: 0 !important;
    }

    /* Show overlay when sidebar is open on mobile */
    .flyout-mobile-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 30;
      display: none;
    }

    body:not(.toggle-sidebar) .flyout-mobile-overlay {
      display: block;
    }

    /* Adjust flyout positioning on mobile */
    .flyout-submenu {
      left: 80px !important;
    }
  }

  /* RESPONSIVE: Desktop/Tablet screens (>= 768px) */
  @media (min-width: 768px) {
    /* Sidebar visible by default on desktop */
    .flyout-sidebar {
      transform: translateX(0);
    }

    /* When toggled closed on desktop */
    body.toggle-sidebar .flyout-sidebar {
      transform: translateX(-260px);
    }

    /* Main content adjusts based on sidebar state */
    .flyout-main-content {
      margin-left: 260px;
    }

    body.toggle-sidebar .flyout-main-content {
      margin-left: 0;
    }

    /* No overlay on desktop */
    .flyout-mobile-overlay {
      display: none !important;
    }
  }
</style>
`;

// Icon mapping helper
const getIconForNode = (nodeName) => {
  const name = nodeName?.toLowerCase() || '';
  if (name.includes('home') || name.includes('dashboard')) return Home;
  if (name.includes('user') || name.includes('customer')) return Users;
  if (name.includes('report') || name.includes('analytics')) return BarChart;
  if (name.includes('product') || name.includes('inventory')) return Package;
  if (name.includes('order') || name.includes('sales')) return ShoppingCart;
  if (name.includes('setting') || name.includes('config')) return Settings;
  if (name.includes('document') || name.includes('file')) return FileText;
  return Briefcase;
};

const Dashboard = ({ children }) => {
  const { user } = useAuth();
  const { sideBarData, loadSideBars } = useMasterDataContext();
  const [input, setInput] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [hoveredParent, setHoveredParent] = useState(null);
  const [hoveredChild, setHoveredChild] = useState(null);
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0 });
  const [childFlyoutPosition, setChildFlyoutPosition] = useState({ top: 0 });
  const [filteredMenu, setFilteredMenu] = useState([]);
  const [searchMenu, setSearchMenu] = useState([]);
  
  // Refs to track hover state and prevent flickering
  const parentTimerRef = useRef(null);
  const childTimerRef = useRef(null);

  // Initialize sidebar state based on screen size
  useEffect(() => {
    const initializeSidebarState = () => {
      const isMobile = window.innerWidth < 768;
      
      if (isMobile) {
        // Mobile: sidebar hidden by default
        document.body.classList.add('toggle-sidebar');
        setIsSidebarOpen(false);
      } else {
        // Desktop: sidebar visible by default
        document.body.classList.remove('toggle-sidebar');
        setIsSidebarOpen(true);
      }
    };

    // Initialize on mount
    initializeSidebarState();

    // Handle window resize
    const handleResize = () => {
      initializeSidebarState();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for toggle-sidebar class changes on body
  useEffect(() => {
    const checkSidebarState = () => {
      const isToggled = document.body.classList.contains('toggle-sidebar');
      setIsSidebarOpen(!isToggled);
      
      // Close all flyout menus when sidebar is toggled
      if (isToggled) {
        setHoveredParent(null);
        setHoveredChild(null);
      }
    };

    // Initial check
    checkSidebarState();

    // Watch for class changes
    const observer = new MutationObserver(checkSidebarState);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const filterMenu = () => {
    if (!sideBarData?.length || !user?.roles) return [];
    return sideBarData
      .filter(parent =>
        parent?.roleNames?.some(role =>
          user.roles.some(ur => ur.toLowerCase() === role.toLowerCase())
        )
      )
      .map(parent => {
        const filteredChildren = parent?.childNodes
          ?.map(child => {
            const filteredGrandChildren = child?.grandChildNodes
              ?.filter(gc =>
                gc?.roleNames?.some(role =>
                  user.roles.some(ur => ur.toLowerCase() === role.toLowerCase())
                )
              ) || [];

            if (
              child?.roleNames?.some(role =>
                user.roles.some(ur => ur.toLowerCase() === role.toLowerCase())
              ) &&
              (child.nodeUrl || filteredGrandChildren.length > 0)
            ) {
              return { ...child, grandChildNodes: filteredGrandChildren };
            }
            return null;
          })
          .filter(Boolean);

        return { ...parent, childNodes: filteredChildren };
      })
      .filter(parent => parent.childNodes?.length > 0);
  };

  useEffect(() => {
    console.log("i am run");
    loadSideBars();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const menu = filterMenu();
      const q = (input || "").trim().toLowerCase();

      if (!q) {
        setFilteredMenu(menu);
        setSearchMenu(menu);
        return;
      }

      let filter = menu.filter(
        (f) => f?.nodeName?.toLowerCase().includes(q)
      );

      if (filter.length === 0) {
        filter = menu.filter(
          (f) =>
            Array.isArray(f?.childNodes) &&
            f.childNodes.some((c) => c?.nodeName?.toLowerCase().includes(q))
        );
      }

      setFilteredMenu(filter);
      setSearchMenu(menu);
    }, 300);

    return () => clearTimeout(timer);
  }, [sideBarData, user?.roles, input]);

  const handleParentEnter = (nodeId, event) => {
    // Don't show flyout if sidebar is collapsed
    if (document.body.classList.contains('toggle-sidebar')) {
      return;
    }

    if (parentTimerRef.current) {
      clearTimeout(parentTimerRef.current);
    }
    
    setHoveredParent(nodeId);
    setHoveredChild(null);
    const rect = event.currentTarget.getBoundingClientRect();
    setFlyoutPosition({ top: rect.top -50});
  };

  const handleParentLeave = () => {
    parentTimerRef.current = setTimeout(() => {
      setHoveredParent(null);
      setHoveredChild(null);
    }, 200);
  };

  const handleFlyoutEnter = () => {
    if (parentTimerRef.current) {
      clearTimeout(parentTimerRef.current);
    }
  };

  const handleFlyoutLeave = () => {
    setHoveredParent(null);
    setHoveredChild(null);
  };

  const handleChildEnter = (nodeId, event) => {
    if (childTimerRef.current) {
      clearTimeout(childTimerRef.current);
    }
    
    setHoveredChild(nodeId);
    const rect = event.currentTarget.getBoundingClientRect();
    setChildFlyoutPosition({ top: rect.top -50});
  };

  const handleChildLeave = () => {
    childTimerRef.current = setTimeout(() => {
      setHoveredChild(null);
    }, 200);
  };

  const handleChildFlyoutEnter = () => {
    if (childTimerRef.current) {
      clearTimeout(childTimerRef.current);
    }
  };

  const handleChildFlyoutLeave = () => {
    setHoveredChild(null);
  };

  // Auto-close sidebar on navigation link click for mobile
  const handleNavLinkClick = () => {
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile && !document.body.classList.contains('toggle-sidebar')) {
      // Close sidebar on mobile when a link is clicked
      document.body.classList.add('toggle-sidebar');
      setIsSidebarOpen(false);
      
      // Also close all flyout menus
      setHoveredParent(null);
      setHoveredChild(null);
    }
  };

  return (
    <>
      <div dangerouslySetInnerHTML={{ __html: sidebarStyles }} />
      
      <div className="flyout-sidebar-wrapper">
        {/* Mobile Toggle */}
        <button
          className="flyout-mobile-toggle"
          onClick={() => {
            setIsSidebarOpen(!isSidebarOpen);
            document.body.classList.toggle('toggle-sidebar');
          }}
        >
          {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Sidebar */}
        <div className={`flyout-sidebar ${!isSidebarOpen ? 'closed' : ''}`}>
          {/* Search */}
          <div className="flyout-search-container">
            <div className="flyout-search-wrapper">
              <Search className="flyout-search-icon" size={18} />
              <Form.Control
                type="text"
                placeholder="Search menu..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="flyout-search-input"
              />
            </div>
          </div>

          {/* Menu Items */}
          <div className="flyout-menu-container">
            <nav>
              {filteredMenu.map(parent => {
                const ParentIcon = getIconForNode(parent.nodeName);
                return (
                  <div
                    key={parent.nodeId}
                    className="flyout-menu-item-wrapper"
                  >
                    <div 
                      className="flyout-menu-item"
                      onMouseEnter={(e) => handleParentEnter(parent.nodeId, e)}
                      onMouseLeave={handleParentLeave}
                    >
                      <div className="flyout-menu-item-content">
                        <ParentIcon size={20} className="flyout-menu-item-icon" />
                        <span className="flyout-menu-item-text">{parent.nodeName}</span>
                      </div>
                      <ChevronRight size={16} className="flyout-menu-item-chevron" />
                    </div>

                    {/* Flyout Menu */}
                    {hoveredParent === parent.nodeId && parent.childNodes?.length > 0 && (
                      <div
                        className="flyout-submenu"
                        style={{
                          left: '260px',
                          top: `${flyoutPosition.top}px`
                        }}
                        onMouseEnter={handleFlyoutEnter}
                        onMouseLeave={handleFlyoutLeave}
                      >
                        {parent.childNodes.map(child => {
                          const ChildIcon = getIconForNode(child.nodeName);
                          const hasGrandChildren = child.grandChildNodes?.length > 0;

                          return (
                            <div key={child.nodeId}>
                              {child.nodeUrl ? (
                                <NavLink
                                  to={child.nodeUrl}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavLinkClick();
                                  }}
                                  className={({ isActive }) =>
                                    `flyout-submenu-item ${isActive ? 'active' : ''}`
                                  }
                                  onMouseEnter={(e) => hasGrandChildren && handleChildEnter(child.nodeId, e)}
                                  onMouseLeave={hasGrandChildren ? handleChildLeave : undefined}
                                >
                                  <div className="flyout-submenu-item-content">
                                    <ChildIcon size={18} />
                                    <span style={{ fontWeight: 500 }}>{child.nodeName}</span>
                                  </div>
                                  {hasGrandChildren && <ChevronRight size={16} />}
                                </NavLink>
                              ) : (
                                <div 
                                  className="flyout-submenu-item"
                                  onMouseEnter={(e) => hasGrandChildren && handleChildEnter(child.nodeId, e)}
                                  onMouseLeave={hasGrandChildren ? handleChildLeave : undefined}
                                >
                                  <div className="flyout-submenu-item-content">
                                    <ChildIcon size={18} />
                                    <span style={{ fontWeight: 500 }}>{child.nodeName}</span>
                                  </div>
                                  {hasGrandChildren && <ChevronRight size={16} />}
                                </div>
                              )}

                              {/* Nested Flyout for Grand Children */}
                              {hoveredChild === child.nodeId && hasGrandChildren && (
                                <div
                                  className="flyout-submenu"
                                  style={{
                                    left: '500px',
                                    top: `${childFlyoutPosition.top}px`
                                  }}
                                  onMouseEnter={handleChildFlyoutEnter}
                                  onMouseLeave={handleChildFlyoutLeave}
                                >
                                  {child.grandChildNodes.map(grand => {
                                    const GrandIcon = getIconForNode(grand.nodeName);
                                    return (
                                      <NavLink
                                        key={grand.nodeId}
                                        to={grand.nodeUrl}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleNavLinkClick();
                                        }}
                                        className={({ isActive }) =>
                                          `flyout-submenu-item ${isActive ? 'active' : ''}`
                                        }
                                      >
                                        <div className="flyout-submenu-item-content">
                                          <GrandIcon size={16} />
                                          <span style={{ fontSize: '14px', fontWeight: 500 }}>{grand.nodeName}</span>
                                        </div>
                                      </NavLink>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

      

        {/* Mobile Overlay */}
        <div
          className="flyout-mobile-overlay"
          onClick={() => {
            setIsSidebarOpen(false);
            document.body.classList.add('toggle-sidebar');
          }}
        />
      </div>
    </>
  );
};

export default Dashboard;